// src/utils/refreshToken.js

import { gql } from "@apollo/client";
import { authStore } from "./tokenStore";
import { logOut } from "./functions";
let refreshPromise = null;

const REFRESH_MUTATION = gql`
  mutation RefreshToken($refreshToken: String!) {
    refreshJwtAuthToken(input: { jwtRefreshToken: $refreshToken }) {
      authToken
    }
  }
`;

// Query to fetch new expiration using user database ID
const GET_USER_EXPIRATION_QUERY = gql`
  query GetUserExpiration($id: ID!) {
    user(id: $id, idType: DATABASE_ID) {
      jwtAuthExpiration
    }
  }
`;

const hasJwtErrorText = (text = "") => {
  const m = String(text).toLowerCase();
  return (
    m.includes("expired token") ||
    m.includes("invalid-secret-key") ||
    m.includes("invalid token") ||
    m.includes("jwt") ||
    m.includes("signature")
  );
};

/**
 * Check if error message indicates auth/token failure
 * In production, servers often return "Internal server error" when JWT is invalid
 */
const hasAuthRelatedErrorMessage = (message = "") => {
  if (!message) return false;
  const m = String(message).toLowerCase();
  // Check for JWT-related messages
  if (hasJwtErrorText(m)) return true;
  // In production, token errors often show as "Internal server error"
  // paired with refresh token context
  if (m.includes("internal server error")) return true;
  if (m.includes("internal error")) return true;
  if (m.includes("server error")) return true;
  return false;
};

/**
 * Robust JWT error detection - checks multiple indicators
 * Works with or without GraphQL debug mode
 * Detects production "Internal server error" as auth failure
 */
const isJwtAuthError = (error) => {
  if (!error) return false;

  // Check 1: GraphQL error extensions (most reliable)
  if (error.extensions) {
    const code = error.extensions.code?.toLowerCase() || "";
    const category = error.extensions.category?.toLowerCase() || "";

    if (
      code.includes("unauthenticated") ||
      code.includes("forbidden") ||
      code.includes("auth") ||
      code.includes("jwt") ||
      category.includes("auth")
    ) {
      console.log("[isJwtAuthError] ✅ Detected via error.extensions.code:", {
        code,
        category,
      });
      authStore.clear();
      logOut();
      return true;
    }
  }

  // Check 2: Error message text (includes production "Internal server error")
  if (error.message && hasAuthRelatedErrorMessage(error.message)) {
    console.log(
      "[isJwtAuthError] ✅ Detected via error.message:",
      error.message
    );
    authStore.clear();
    logOut();
    return true;
  }

  // Check 3: Error status code (if available)
  if (error.status === 401 || error.statusCode === 401) {
    console.log("[isJwtAuthError] ✅ Detected via HTTP 401 Unauthorized");
    return true;
  }

  // Check 4: GraphQL error originalError (underlying HTTP error)
  if (error.originalError) {
    if (
      error.originalError.status === 401 ||
      error.originalError.statusCode === 401
    ) {
      console.log("[isJwtAuthError] ✅ Detected via originalError HTTP 401");
      authStore.clear();
      logOut();
      return true;
    }
    // Check originalError message too (may contain "Internal server error")
    if (hasAuthRelatedErrorMessage(error.originalError.message)) {
      console.log(
        "[isJwtAuthError] ✅ Detected via originalError message:",
        error.originalError.message
      );
      authStore.clear();
      logOut();
      return true;
    }
  }

  return false;
};

/**
 * performRefresh(client)
 * - client: ApolloClient instance (must be the same client as your app)
 *
 * This function:
 * - Runs the refresh mutation (skipRetryLink)
 * - If authToken returned: saves token immediately
 * - Then queries the user's jwtAuthExpiration with network-only (skipRetryLink)
 * - Saves new expiration and returns the new token
 */
export const performRefresh = async (client) => {
  const auth = authStore.load();
  if (!auth?.refreshToken) {
    if (process.env.NODE_ENV !== "production") {
      console.log("[performRefresh] ❌ No refresh token stored");
    }
    // Clear any stale auth state
    try {
      authStore.clear();
      logOut();
    } catch (e) {
      // ignore
    }
    return null;
  }

  // Avoid parallel refreshes
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    if (process.env.NODE_ENV !== "production") {
      console.log("[performRefresh] 🔄 Starting refresh flow...");
    }

    try {
      // 1) Execute refresh mutation (skip retry link to avoid recursion)
      const { data: refreshData, errors: refreshErrors } = await client.mutate({
        mutation: REFRESH_MUTATION,
        variables: { refreshToken: auth.refreshToken },
        fetchPolicy: "no-cache",
        context: { skipRetryLink: true },
      });

      if (process.env.NODE_ENV !== "production") {
        console.log("[performRefresh] refresh mutation result:", {
          refreshData,
          refreshErrors,
        });
      }

      // If GraphQL-level errors exist, check if they're JWT-related and fail
      if (refreshErrors?.length) {
        if (process.env.NODE_ENV !== "production") {
          console.log(
            "[performRefresh] refresh mutation errors:",
            refreshErrors
          );
        }
        // Check each error for JWT/auth issues
        const jwtError = refreshErrors.find((err) => isJwtAuthError(err));
        if (jwtError) {
          throw new Error(
            "Fatal refresh error (refresh mutation returned JWT/auth error)"
          );
        }
      }

      const newAuthToken = refreshData?.refreshJwtAuthToken?.authToken;
      if (!newAuthToken) {
        if (process.env.NODE_ENV !== "production") {
          console.log(
            "[performRefresh] ❗ No authToken returned from refresh mutation"
          );
        }
        throw new Error("Invalid refresh payload (no authToken)");
      }

      // Immediately save the new token (so subsequent requests use it)
      const updatedAuth = {
        ...auth,
        authToken: newAuthToken,
      };
      authStore.save(updatedAuth);
      if (process.env.NODE_ENV !== "production") {
        console.log("[performRefresh] ✅ Saved new authToken");
      }

      // 2) Now fetch new jwtAuthExpiration using the user.databaseId (network-only)
      const userId = auth?.user?.databaseId;
      if (!userId) {
        if (process.env.NODE_ENV !== "production") {
          console.log(
            "[performRefresh] ⚠️ No user.databaseId available; skipping expiration fetch"
          );
        }
        return newAuthToken;
      }

      const { data: expirationData, errors: expErrors } = await client.query({
        query: GET_USER_EXPIRATION_QUERY,
        variables: { id: userId },
        fetchPolicy: "network-only",
        context: { skipRetryLink: true },
      });

      if (process.env.NODE_ENV !== "production") {
        console.log("[performRefresh] expiration query result:", {
          expirationData,
          expErrors,
        });
      }

      if (expErrors?.length) {
        if (process.env.NODE_ENV !== "production") {
          console.log("[performRefresh] expiration query errors:", expErrors);
        }
        // Check each error for JWT/auth issues
        const jwtError = expErrors.find((err) => isJwtAuthError(err));
        if (jwtError) {
          throw new Error(
            "Fatal refresh error (expiration query returned JWT/auth error)"
          );
        }
      }

      const newJwtAuthExpiration = expirationData?.user?.jwtAuthExpiration;
      if (newJwtAuthExpiration) {
        const finalAuth = {
          ...updatedAuth,
          jwtAuthExpiration: newJwtAuthExpiration,
        };
        authStore.save(finalAuth);
        if (process.env.NODE_ENV !== "production") {
          console.log(
            "[performRefresh] ✅ Saved new jwtAuthExpiration:",
            newJwtAuthExpiration
          );
        }
      } else {
        if (process.env.NODE_ENV !== "production") {
          console.log("[performRefresh] ⚠️ expiration not found in response");
        }
      }

      return newAuthToken;
    } catch (err) {
      // Determine if this is a JWT/auth error (logout required) or other error (retry possible)
      const isAuthError = isJwtAuthError(err);

      if (process.env.NODE_ENV !== "production") {
        console.error(
          `[performRefresh] 🔴 Refresh failed (${
            isAuthError ? "auth error - logout" : "other error"
          }):`,
          err?.message || err
        );
      } else {
        // In production, log the error type for debugging
        console.error(
          `[performRefresh] 🔴 Refresh failed:`,
          err?.message || err
        );
      }

      // Always clear auth on any refresh failure (safest approach)
      // In production, failed refresh = invalid/expired token
      try {
        authStore.clear();
        logOut();
        if (typeof window !== "undefined") {
          try {
            window.localStorage.removeItem("auth");
          } catch (e) {
            // ignore
          }
        }
        if (process.env.NODE_ENV !== "production") {
          console.log("[performRefresh] ✅ Auth cleared");
        }
      } catch (e) {
        console.error("[performRefresh] Error clearing auth:", e);
      }

      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};
